<template>
    <div class="fixed-center q-pa-md card">
        <div class="row justify-center">
            <q-spinner-gears size="70px" color="grey" />
        </div>
        <div class="row justify-center">
            <div class="q-mt-md row justify-center">
                <p style="font-size: 22px">{{ $t('Redirecting') }}</p>
            </div>
        </div>
        <div class="q-mb-lg row justify-center">
            {{ $t('Please wait while you are being redirected.') }}
        </div>

        <q-separator class="q-mb-md" />
        <div class="q-mb-md row justify-center text-secondary">
            {{ $t('If you are not redirected automatically, click here') }}
        </div>
        <div class="row justify-center">
            <q-btn
                color="secondary"
                style="width: 200px"
                flat
                @click="redirectUserToDashboard()"
                :label="$t('Redirect')"
            />
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'OAuthComplete',
    mounted() {
        const state = this.$route.query.state;
        const code = this.$route.query.code;

        // If the state is source_mobile, redirect to deep link with code
        // The app listener will handle the deep link redirect. See @boot/capacitorPlugins.js
        if (state === 'source_mobile') {
            window.location.href = `iotorqlean://oauth/complete/?code=${code}`;
            return;
        }

        // Process the code if it exists
        if (code) {
            this.exchangeCodeForToken(code);
        } else {
            this.$notifyError(
                this.$t('Invalid state or code. Please try again.')
            );
            this.logout();
        }
    },
    computed: {
        ...mapState(useAuthStore, {
            getCodeVerifier: 'codeVerifier',
            getProviderType: 'providerType'
        }),
        /**
         * Get the redirect URI. This URI will  have to be registered in the identity provider's
         * application settings.
         * @returns {string}
         */
        redirectURI() {
            // If in a mobile environment, build the redirect URI using the tenant
            if (this.$q.platform.is.capacitor) {
                const tenant =
                    this.$pinia.state.value.auth?.tenant?.toLowerCase();

                const tenantURL = `https://${tenant}.iotorq-lean.com`;
                return `${tenantURL}/oauth/complete/`;
            }

            return `${window.location.origin}/oauth/complete/`;
        }
    },
    methods: {
        ...mapActions(useAuthStore, [
            'getCsrfTokenAction',
            'loadCurrentUserAction',
            'loadSubscribedCausesAction',
            'logoutAction',
            'setJWTAction'
        ]),
        /**
         * Logout the user.
         */
        async logout() {
            this.logoutAction();
            this.$router.push({
                name: 'login'
            });
        },
        /**
         * Get the user's details and load it in the store.
         */
        async loadUser() {
            try {
                await this.loadCurrentUserAction();
                await this.loadSubscribedCausesAction();
            } catch (error) {
                this.$notifyError(
                    this.$t(
                        'Failed to load user profile. Please try again later.'
                    ),
                    error
                );
            }
        },
        /**
         * Redirect user to dashboard.
         */
        async redirectUserToDashboard() {
            await this.loadUser();
            // Redirect to the facility list page
            this.$router.push({
                name: 'facility_list'
            });
        },
        /**
         * Store the user's authentication tokens in the store.
         * @param {String} access
         * @param {String} refresh
         */
        storeAuthTokens(access, refresh) {
            // set tokens in store
            const tokens = {
                access: access,
                refresh: refresh
            };
            this.setJWTAction(tokens);
        },
        /**
         * Exchange the authorization code for a JWT token.
         * @param {String} code
         */
        async exchangeCodeForToken(code) {
            const codeVerifier = this.getCodeVerifier;
            const providerType = this.getProviderType;

            // set CSRF token in header
            this.$axios.defaults.headers.common['X-CSRFToken'] =
                await this.getCsrfTokenAction();

            try {
                const response = await this.$axios.post(
                    `/auth/oauth/${providerType}/exchange`,
                    {
                        code: code,
                        code_verifier: codeVerifier,
                        redirect_uri: this.redirectURI
                    },
                    {
                        disableRequestInterceptor: true
                    }
                );
                const { refresh, access } = response.data;
                this.storeAuthTokens(access, refresh);
                await this.redirectUserToDashboard();
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                if (error.code === 'no_associated_user') {
                    this.$notifyError(
                        this.$t(
                            'The email address provided is not linked to any ioTORQ LEAN account.'
                        ),
                        error
                    );
                } else if (error.code === 'user_not_active') {
                    this.$notifyError(
                        this.$t('The user account is not active.'),
                        error
                    );
                } else if (error.error_code === 'account_lockout') {
                    this.$notifyError(
                        this.$t(
                            "Too many login attempts with this username. Please try again in 30 minutes or contact support{'@'}iotorq-lean.com for immediate assistance."
                        ),
                        error
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Failed to authenticate user. Please try again.'
                        ),
                        error
                    );
                }
                this.logout();
            }
        }
    }
};
</script>

<style scoped>
.card {
    height: 200px;
}
</style>
